import { useMemo, useState } from "react";
import {
  getNextSortConfig,
  SortConfig,
} from "@src/cloud-connect/utils/sort-table-columns";

/**
 * Generic hook to provide sorting functionality for OTA history lists.
 *
 * @template T - Type of list item (must be an object with string keys)
 * @param list - The original unsorted list
 *
 * @returns
 *  - sortedRows: Memoized sorted list based on current sort config
 *  - sortConfig: Current sorting configuration (key + direction)
 *  - handleSort: Function to trigger sorting for a specific column
 */
export function useSortableOtaHistoryList<T extends Record<string, any>>(
  list: T[],
) {
  // Holds the current sorting configuration (column key + direction)
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(null);

  /**
   * Updates sorting configuration when a column header is clicked.
   * Delegates direction toggle logic to getNextSortConfig utility.
   */
  const handleSort = (key: keyof T) => {
    const newConfig = getNextSortConfig(sortConfig, key);
    setSortConfig(newConfig);
  };

  /**
   * Memoized sorted list.
   * - Returns original list if no sort is applied.
   * - Creates a shallow copy before sorting to avoid mutating the original list.
   * - Performs case-insensitive string comparison.
   */
  const sortedRows = useMemo(() => {
    if (!sortConfig) return list;

    return [...list].sort((a, b) => {
      const aValue = a[sortConfig.key]?.toString().toLowerCase();
      const bValue = b[sortConfig.key]?.toString().toLowerCase();

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;

      return 0; // values are equal
    });
  }, [list, sortConfig]);

  return {
    sortedRows,
    sortConfig,
    handleSort,
  };
}
